import { gql } from "graphql-request";
import { createGraphQLClient } from "./graphql.service";
import { z } from "zod";
import { DATA_TYPE_VALUES, FORM_TYPE_VALUES } from "./entity.service";

const dataTypeValues = DATA_TYPE_VALUES;

const formTypeValues = FORM_TYPE_VALUES;

const fieldSchema = z.object({
  id: z.string(),
  name: z.string(),
  width: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  height: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  type: z.union([z.enum(dataTypeValues), z.literal("relation")]).optional(),
  icon: z.union([z.enum(dataTypeValues), z.literal("relation"), z.enum(formTypeValues)]).optional(),
  relation_field_to_display: z.string().optional(),
  mapping: z
    .object({
      id: z.string(),
      component: z.string(),
      config: z.union([
        z.record(z.string(), z.string()),
        z.array(
          z.object({
            type: z.string(),
            componentField: z.string(),
            entityFields: z.array(z.string()),
          }),
        ),
      ]),
      fieldsToFetch: z.array(z.string()),
    })
    .optional(),
});

const sectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  fields: z.array(fieldSchema),
});

export type Field = z.infer<typeof fieldSchema>;
export type Section = z.infer<typeof sectionSchema>;

export interface EntityConnectInput {
  id: string;
}

export interface CreateEntityViewInput {
  name: string;
  description?: string;
  entity?: EntityConnectInput;
  pylo_aros?: Array<EntityConnectInput>;
}

export interface UpdateEntityViewInput {
  id: string;
  name?: string;
  description?: string;
  entity?: EntityConnectInput;
  section_config?: Section[];
  condition?: string;
  pylo_aros?: Array<EntityConnectInput>;
}

export interface EntityView {
  id: string;
  name?: string;
  description?: string;
  entity?: EntityConnectInput;
  section_config?: Section[];
  condition?: string;
  pylo_aros?: Array<EntityConnectInput>;
}

export interface EntityViewListResponse {
  data: EntityView[];
}

const entityViewListQueryDocument = gql`
  query EntityViewList($entityId: String) {
    entityViewList(
      filter: { query: { condition: { field: "entity.id", operator: equal, value: $entityId } } }
    ) {
      data {
        id
        name
        description
        section_config
        condition
        entity {
          data {
            id
          }
        }
        pylo_aros {
          data {
            id
            entity {
              data {
                name
              }
            }
            pylo_users {
              data {
                email
                pylo_api_key {
                  data {
                    name
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const createEntityViewMutationDocument = gql`
  mutation CreateEntityView(
    $name: String!
    $description: String
    $entity: EntityInput! # $pyloAros: [EntityInput!]
  ) {
    createEntityView(
      input: {
        name: $name
        description: $description
        entity_set: $entity
        # pylo_aros: $pyloAros
      }
    ) {
      data {
        id
        name
        description
        section_config
        entity {
          data {
            id
          }
        }
        pylo_aros {
          data {
            id
            entity {
              data {
                name
              }
            }
            pylo_users {
              data {
                email
                pylo_api_key {
                  data {
                    name
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const updateEntityViewMutationDocument = gql`
  mutation UpdateEntityView($input: UpdateEntityViewInput!) {
    updateEntityView(input: $input) {
      data {
        id
        name
        description
        section_config
        condition
        entity {
          data {
            id
          }
        }
        pylo_aros {
          data {
            id
            entity {
              data {
                name
              }
            }
            pylo_users {
              data {
                email
                pylo_api_key {
                  data {
                    name
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const getEntityViewByIdQueryDocument = gql`
  query GetEntityViewById($id: ID!) {
    entityViewById(id: $id) {
      data {
        id
        name
        description
        section_config
        condition
        entity {
          data {
            id
          }
        }
        pylo_aros {
          data {
            id
            entity {
              data {
                name
              }
            }
            pylo_users {
              data {
                email
                pylo_api_key {
                  data {
                    name
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getEntityViewList = async (entityId?: string) => {
  // TODO: lets input the default view, the isRootAdmin and the users aros here
  try {
    const client = createGraphQLClient();
    const result = (await client.request(entityViewListQueryDocument, {
      entityId,
    })) as {
      entityViewList: {
        data: Array<Omit<EntityView, "section_config"> & { section_config?: string }>;
      };
    };
    return {
      data: result.entityViewList.data.map((view) => {
        // @ts-expect-error FIXME: fix the type of the EntityView
        const pyloArosData = view.pylo_aros?.data ?? [];
        return {
          ...view,
          section_config: view.section_config
            ? sectionSchema.array().parse(JSON.parse(view.section_config))
            : undefined,
          // @ts-expect-error FIXME: fix the type of the EntityView
          pylo_aros: pyloArosData.map((aro) => ({ id: aro.id })),
        };
      }),
    };
  } catch (error) {
    console.error("error fetching entity views", error);
    throw error;
  }
};

export const createEntityView = async ({
  name,
  description,
  entity,
  // pylo_aros,
}: CreateEntityViewInput) => {
  const client = createGraphQLClient();

  try {
    const result = (await client.request(createEntityViewMutationDocument, {
      name,
      description,
      entity,
      // FIXME: why is this not working? Is it working by now? Please check.
      // pyloAros: pylo_aros?.map((aro) => ({ id: aro.id })), //TODO: add this back in once it works
    })) as {
      createEntityView: {
        data: Omit<EntityView, "section_config"> & { section_config?: string };
      };
    };

    return {
      ...result.createEntityView.data,
      section_config: result.createEntityView.data.section_config
        ? sectionSchema.array().parse(JSON.parse(result.createEntityView.data.section_config))
        : undefined,
    };
  } catch (error) {
    console.error("error creating entity view", error);
    throw error;
  }
};

export const updateEntityView = async ({
  id,
  name,
  description,
  entity,
  section_config,
  condition,
  pylo_aros,
}: UpdateEntityViewInput) => {
  const client = createGraphQLClient({
    endpointName: "updateEntityView",
  });

  try {
    const variables: {
      id: string;
      name?: string;
      description?: string;
      entity?: EntityConnectInput;
      section_config?: string;
      condition?: string;
      pylo_aros_set?: Array<{ id: string }>;
    } = {
      id,
      name,
      description,
      section_config: section_config ? JSON.stringify(section_config) : undefined,
      condition,
      pylo_aros_set: pylo_aros?.map((aro) => ({ id: aro.id })),
    };

    if (entity !== undefined) {
      variables.entity = entity;
    }

    // For now, we'll use connect for all AROs since we don't have the current state
    // In a more sophisticated implementation, we would compare with the current AROs

    const result = (await client.request(updateEntityViewMutationDocument, {
      input: variables,
    })) as {
      updateEntityView: {
        data: Omit<EntityView, "section_config"> & { section_config?: string };
      };
    };

    return {
      ...result.updateEntityView.data,
      section_config: result.updateEntityView.data.section_config
        ? sectionSchema.array().parse(JSON.parse(result.updateEntityView.data.section_config))
        : undefined,
    };
  } catch (error) {
    console.error("error updating entity view", error);
    throw error;
  }
};

export const getEntityViewById = async (id?: string) => {
  const client = createGraphQLClient({
    endpointName: "entityViewById",
  });

  try {
    const result = (await client.request(getEntityViewByIdQueryDocument, {
      id,
    })) as {
      entityViewById: {
        data: Omit<EntityView, "section_config"> & { section_config?: string };
      };
    };

    const pyloArosData =
      // @ts-expect-error FIXME: fix the type of the EntityView
      result.entityViewById.data.pylo_aros?.data ?? [];

    return {
      ...result.entityViewById.data,
      section_config: result.entityViewById.data.section_config
        ? sectionSchema.array().parse(JSON.parse(result.entityViewById.data.section_config))
        : undefined,
      // @ts-expect-error FIXME: fix the type of the EntityView
      pylo_aros: pyloArosData.map((aro) => ({ id: aro.id })),
    };
  } catch (error) {
    console.error("error fetching entity view by id", error);
    throw error;
  }
};
